import { GridRowClickEvent } from "@progress/kendo-react-grid";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { STFF } from "@/models/stff.tsx";
import { forwardRef, useImperativeHandle } from "react";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type StaffAssignment = {
  PreschoolCode?: string;
  ProgramYear?: string;
  [key: string]: any;
};

type StaffWizardSearchResultsProps = {
  searchResults: STFF[];
  searchedName?: string;
  onSelectStaff: (staff: STFF) => void;
  onCreateNew: () => void;
  onBack: () => void;
  canCreateNew: boolean;
  preschools: PreschoolSummary[];
};

export type StaffSearchResultsRef = {
  handleBack: () => void;
};

export const StaffWizardSearchResults = forwardRef<
  StaffSearchResultsRef,
  StaffWizardSearchResultsProps
>(function StaffWizardSearchResults(
  {
    searchResults,
    searchedName,
    onSelectStaff,
    onCreateNew,
    onBack,
    canCreateNew,
    preschools,
  },
  ref
) {
  const resultsCount = searchResults.length;

  const handleRowClick = (event: GridRowClickEvent) => {
    const staff: STFF = event.dataItem.__raw as STFF;
    onSelectStaff(staff);
  };

  const handleBackClick = () => {
    onBack();
  };

  useImperativeHandle(ref, () => ({
    handleBack: handleBackClick,
  }));

  const getPreschoolName = (code: string | undefined): string => {
    if (!code) return "";
    const preschool = preschools.find((p) => p.PreschoolCDSCode === code);
    return preschool?.PreschoolName || code;
  };

  const getPreschoolFromStaff = (staff: STFF): string => {
    const assignments = (staff as any).StaffAssignments as
      | StaffAssignment[]
      | undefined;

    if (assignments && Array.isArray(assignments) && assignments.length > 0) {
      const preschoolCode = assignments[0]?.PreschoolCode;
      if (preschoolCode) {
        return getPreschoolName(preschoolCode);
      }
    }

    if ((staff as any).PreschoolCode) {
      return getPreschoolName((staff as any).PreschoolCode);
    }

    return "";
  };

  const columns = [
    { field: "LocalStaffId", title: "Staff Local ID", width: "160px" },
    { field: "StaffLegalLastName", title: "Last Name", width: "140px" },
    { field: "StaffLegalFirstName", title: "First Name", width: "140px" },
    { field: "PreschoolName", title: "Preschool", width: "200px" },
    { field: "ProgramYear", title: "Program Year", width: "120px" },
  ];

  const tableData = searchResults.map((record) => ({
    LocalStaffId: record.LocalStaffId || "",
    StaffLegalLastName: record.StaffLegalLastName || "",
    StaffLegalFirstName: record.StaffLegalFirstName || "",
    PreschoolName: getPreschoolFromStaff(record),
    ProgramYear: record.ProgramYear || "",
    __raw: record,
  }));

  return (
    <div style={{ padding: "0 24px 24px", background: "white" }}>
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <h2
          style={{
            fontWeight: "600",
            marginBottom: "12px",
            fontSize: "16px",
            color: "#333",
          }}
        >
          {searchedName && searchedName !== "Staff" ? (
            <>{searchedName} - </>
          ) : null}
          {resultsCount} Potential Match{resultsCount !== 1 ? "es" : ""} Found
        </h2>
        <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
          Select a record to pull in existing data.
        </p>
      </div>
      <div
        style={{
          background: "white",
          borderRadius: "4px",
        }}
      >
        <div style={{ maxHeight: "200px", overflowY: "auto" }}>
          <KendoTableClientState
            data={tableData}
            columns={columns}
            onRowClick={handleRowClick}
            filterable={false}
            noItemsMessage="No matching staff found"
            initialTake={searchResults.length}
          />
        </div>
      </div>

      {canCreateNew && (
        <>
          <div
            style={{
              position: "relative",
              margin: "20px 0",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                background: "white",
                padding: "0 20px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              Or, if the staff member is not already in the CAPSDAC system:
            </span>
          </div>
          <div style={{ textAlign: "center", marginTop: "32px" }}>
            <button
              type="button"
              onClick={onCreateNew}
              style={{
                padding: "12px 40px",
                background: "var(--color-p3)",
                border: "none",
                borderRadius: "4px",
                color: "white",
                fontSize: "15px",
                cursor: "pointer",
                fontWeight: "600",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Continue with New Record
            </button>
          </div>
        </>
      )}

      {!canCreateNew && searchResults.length === 0 && (
        <div
          style={{
            textAlign: "center",
            marginTop: "32px",
            padding: "16px",
            background: "#f8f9fa",
            borderRadius: "4px",
            border: "1px solid #dee2e6",
          }}
        >
          <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
            No records found. To create a new record, please search again using
            First Name and Last Name.
          </p>
        </div>
      )}
    </div>
  );
});
